import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// A form with unsaved user input registers a guard so that anything about to
// destroy that input can warn first. Several forms may be mounted at once (e.g. a
// detail drawer over a list view), so guards live in a registry.
//
// Two kinds of caller ask (ticket 15, audit F13):
//   * app navigation (sidebar, header) consults EVERY guard — leaving the page
//     really does destroy all of them;
//   * a close button (the order drawer) consults only the guards registered inside
//     its own subtree, marked with a scope token. Asking about a form that lives
//     beside the drawer would both warn about the wrong thing and, on confirm,
//     discard it.
interface UnsavedChangesGuard {
  isDirty: () => boolean;
  // Called after the user confirms leaving — must reset the unsaved state
  discard: () => void;
}

/** Identity-only marker for "the guards registered under this subtree". */
export type UnsavedChangesScopeToken = object;

interface PendingConfirmation {
  /** Runs only if the user confirms leaving, and only once. */
  onConfirm: () => void;
  /** The scope the question was asked about; null = app-wide. */
  scope: UnsavedChangesScopeToken | null;
  /** `location.key` at the time of asking. Only consulted for an app-wide request. */
  routeKey: string;
}

interface UnsavedChangesContextValue {
  /** Called by <UnsavedChangesScope> for as long as it is mounted. */
  registerScope: (scope: UnsavedChangesScopeToken) => () => void;
  registerGuardScoped: (
    guard: UnsavedChangesGuard,
    scope: UnsavedChangesScopeToken | null
  ) => () => void;
  /**
   * Returns true when navigation may proceed right away. When there are
   * unsaved changes it opens the confirmation dialog and returns false —
   * the caller must cancel its own navigation (e.g. e.preventDefault()) and
   * does NOT get its action run for it. onProceed runs only if the user later
   * confirms leaving.
   */
  interceptNavigation: (href: string, onProceed?: () => void) => boolean;
  /** navigate() that respects the guards */
  guardedNavigate: (href: string) => void;
  /**
   * Runs `action` when nothing in `scope` is dirty, otherwise after the user
   * confirms leaving. Unlike interceptNavigation this OWNS the action in both
   * branches — its callers (a drawer close, a history-delta back button) have no
   * href to fall back on. Returns true when the action already ran.
   */
  confirmDiscard: (
    action: () => void,
    scope?: UnsavedChangesScopeToken | null
  ) => boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
  null
);

const ScopeContext = createContext<UnsavedChangesScopeToken | null>(null);

/** A stable token for the lifetime of the calling component. */
export function useUnsavedChangesScope(): UnsavedChangesScopeToken {
  const ref = useRef<UnsavedChangesScopeToken | null>(null);
  if (ref.current === null) ref.current = {};
  return ref.current;
}

/**
 * Marks every guard registered below it as belonging to `token`, and — the part
 * that matters when the user answers — publishes the token's own lifetime. A
 * confirmation asked by a drawer is stale only once the drawer's content is gone,
 * which is exactly how long this component is mounted.
 */
export function UnsavedChangesScope({
  token,
  children,
}: {
  token: UnsavedChangesScopeToken;
  children: React.ReactNode;
}) {
  const context = useContext(UnsavedChangesContext);
  const registerScope = context?.registerScope;
  useEffect(() => registerScope?.(token), [registerScope, token]);
  return <ScopeContext.Provider value={token}>{children}</ScopeContext.Provider>;
}

export function UnsavedChangesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const guardsRef = useRef<
    Map<UnsavedChangesGuard, UnsavedChangesScopeToken | null>
  >(new Map());
  const liveScopesRef = useRef<Set<UnsavedChangesScopeToken>>(new Set());
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  // Read at answer time, never in a dependency array: a route change must not
  // rebuild the callbacks every consumer holds.
  const routeKeyRef = useRef(location.key);
  routeKeyRef.current = location.key;
  /** A navigation the user already confirmed — the blocker must let it through. */
  const proceedingRef = useRef(false);
  // A double-click on "Leave" can re-enter handleLeave before setPending(null)
  // has flushed; the action must run at most once.
  const consumedRef = useRef(false);

  const registerScope = useCallback((scope: UnsavedChangesScopeToken) => {
    liveScopesRef.current.add(scope);
    return () => {
      liveScopesRef.current.delete(scope);
    };
  }, []);

  const registerGuardScoped = useCallback(
    (guard: UnsavedChangesGuard, scope: UnsavedChangesScopeToken | null) => {
      guardsRef.current.set(guard, scope);
      return () => {
        guardsRef.current.delete(guard);
      };
    },
    []
  );

  // Always a live read of the registry: a guard's isDirty() closure reads a ref
  // that keeps returning true long after its component unmounted, so a captured
  // list would answer for forms that no longer exist.
  const dirtyGuards = useCallback(
    (scope: UnsavedChangesScopeToken | null): UnsavedChangesGuard[] => {
      const found: UnsavedChangesGuard[] = [];
      guardsRef.current.forEach((guardScope, guard) => {
        if (scope !== null && guardScope !== scope) return;
        if (guard.isDirty()) found.push(guard);
      });
      return found;
    },
    []
  );

  /** The single entry point. True = nothing to ask about; the caller proceeds. */
  const request = useCallback(
    (onConfirm: () => void, scope: UnsavedChangesScopeToken | null): boolean => {
      if (dirtyGuards(scope).length === 0) return true;
      consumedRef.current = false;
      setPending({ onConfirm, scope, routeKey: routeKeyRef.current });
      return false;
    },
    [dirtyGuards]
  );

  const interceptNavigation = useCallback(
    (href: string, onProceed?: () => void): boolean =>
      request(() => {
        // The guards were discarded a moment ago, but their `isDirty` closures
        // read refs that only catch up on the next render — so tell the blocker
        // this navigation is already answered rather than asking the user twice.
        proceedingRef.current = true;
        navigate(href);
        onProceed?.();
      }, null),
    [request, navigate]
  );

  const guardedNavigate = useCallback(
    (href: string) => {
      if (interceptNavigation(href)) navigate(href);
    },
    [interceptNavigation, navigate]
  );

  const confirmDiscard = useCallback(
    (
      action: () => void,
      scope: UnsavedChangesScopeToken | null = null
    ): boolean => {
      if (!request(action, scope)) return false;
      action();
      return true;
    },
    [request]
  );

  // --- The browser's own Back button ---------------------------------------
  //
  // Back is not a navigation the app initiates, so nothing used to consult the
  // guards: the route changed, the form unmounted and the draft was gone before
  // anyone could ask (BUG-16). The data router hands the navigation over before
  // it happens, which is the only moment the question can still be asked
  // truthfully — and the reason App mounts through `createBrowserRouter`.
  //
  // Everything else follows from that: no history entries of our own, no
  // arithmetic on how far Back was going, and `location.key` keeps meaning what
  // the rest of the app thinks it means.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (proceedingRef.current) return false;
    const moved =
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash;
    return moved && dirtyGuards(null).length > 0;
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const go = () => {
      proceedingRef.current = true;
      blocker.proceed?.();
    };
    // `request` answers true when nothing is dirty after all — then there was
    // never a question to ask and the navigation simply continues.
    if (request(go, null)) go();
  }, [blocker, request]);

  // A navigation landed: whatever exemption we granted is spent.
  useEffect(() => {
    proceedingRef.current = false;
  }, [location.key]);

  // Native browser prompt on refresh/close while any form has unsaved data
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyGuards(null).length > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirtyGuards]);

  const handleStay = () => {
    setPending(null);
    // The router is holding a navigation open behind the dialog; let it go.
    if (blocker.state === "blocked") blocker.reset?.();
  };

  const handleLeave = () => {
    if (!pending || consumedRef.current) return;
    consumedRef.current = true;
    // The confirmation outlives the page it was asked about — the provider sits
    // above <Routes> and the browser's own Back button works underneath a modal —
    // so the confirmed action has to be checked for staleness before it runs.
    //
    // The liveness of the *asker* is the test, never the liveness of a guard: a
    // form's guard comes and goes for reasons that have nothing to do with the
    // drawer around it (a save clears the dirty flag; a foreground refresh after a
    // submit unmounts and remounts the section with a brand-new guard object), and
    // in every one of those cases the drawer is still there and the close the user
    // confirmed must still happen. A scoped request is alive while its scope is
    // mounted; an app-wide one, while the route it was asked on is still current.
    const { onConfirm, scope, routeKey } = pending;
    const alive =
      scope !== null
        ? liveScopesRef.current.has(scope)
        : routeKeyRef.current === routeKey;
    // Re-derived from the live registry, so a guard that unmounted is never asked
    // (its isDirty closure would answer `true` forever) and a remounted one is.
    const dirty = alive ? dirtyGuards(scope) : [];
    setPending(null);
    if (!alive) return;
    dirty.forEach((guard) => guard.discard());
    onConfirm();
  };

  const value = useMemo(
    () => ({
      registerScope,
      registerGuardScoped,
      interceptNavigation,
      guardedNavigate,
      confirmDiscard,
    }),
    [
      registerScope,
      registerGuardScoped,
      interceptNavigation,
      guardedNavigate,
      confirmDiscard,
    ]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}

      <Dialog open={!!pending} onOpenChange={(open) => !open && handleStay()}>
        <DialogContent className="sm:max-w-lg p-8 gap-6">
          <DialogHeader className="gap-3">
            <DialogTitle className="flex items-center gap-2.5">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
              {t("unsavedChanges.title")}
            </DialogTitle>
            <DialogDescription className="leading-relaxed">
              {t("unsavedChanges.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 pt-2">
            <Button variant="outline" onClick={handleStay}>
              {t("unsavedChanges.stay")}
            </Button>
            <Button variant="destructive" onClick={handleLeave}>
              {t("unsavedChanges.leave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

interface UseUnsavedChanges
  extends Omit<UnsavedChangesContextValue, "registerGuardScoped" | "registerScope"> {
  /** Registers a guard in the nearest scope; returns the unregister function */
  registerGuard: (guard: UnsavedChangesGuard) => () => void;
}

const MISSING_PROVIDER =
  "useUnsavedChanges must be used within UnsavedChangesProvider";

export function useUnsavedChanges(): UseUnsavedChanges {
  const context = useContext(UnsavedChangesContext);
  const scope = useContext(ScopeContext);
  const registerGuardScoped = context?.registerGuardScoped;
  // Referential stability matters: every consumer registers its guard in a
  // useEffect keyed on registerGuard, so a new identity per render would
  // re-register on every render. Both deps are stable (a useCallback in the
  // provider, and a ref-held token).
  const registerGuard = useCallback(
    (guard: UnsavedChangesGuard) => {
      if (!registerGuardScoped) throw new Error(MISSING_PROVIDER);
      return registerGuardScoped(guard, scope);
    },
    [registerGuardScoped, scope]
  );
  if (!context) throw new Error(MISSING_PROVIDER);
  return {
    registerGuard,
    interceptNavigation: context.interceptNavigation,
    guardedNavigate: context.guardedNavigate,
    confirmDiscard: context.confirmDiscard,
  };
}
